import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import './OrderForm.css';

const OrderForm = () => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_po_number: '',
    item_name: '',
    item_sku: '',
    item_size: '',
    quantity: 1,
    price: 0,
    payment_status: 'Not Paid',
    salesperson_id: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [notesCharCount, setNotesCharCount] = useState(0);

  // Fetch active users for salesperson dropdown
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/special-orders/users/active');
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    }
  };

  // Format phone number as user types (XXX-XXX-XXXX)
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required';
    } else if (formData.customer_name.length > 255) {
      newErrors.customer_name = 'Customer name cannot exceed 255 characters';
    }

    if (formData.customer_phone && formData.customer_phone.replace(/\D/g, '').length !== 10) {
      newErrors.customer_phone = 'Phone number must be exactly 10 digits';
    }

    if (formData.customer_po_number && formData.customer_po_number.length > 100) {
      newErrors.customer_po_number = 'PO number cannot exceed 100 characters';
    }

    if (!formData.item_name.trim()) {
      newErrors.item_name = 'Item name is required';
    } else if (formData.item_name.length > 255) {
      newErrors.item_name = 'Item name cannot exceed 255 characters';
    }

    if (formData.item_sku && formData.item_sku.length > 100) {
      newErrors.item_sku = 'SKU cannot exceed 100 characters';
    }

    if (formData.item_size && formData.item_size.length > 50) {
      newErrors.item_size = 'Size cannot exceed 50 characters';
    }

    if (!formData.quantity || formData.quantity <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }

    if (!formData.salesperson_id) {
      newErrors.salesperson_id = 'Salesperson is required';
    }

    if (formData.notes && formData.notes.length > 1000) {
      newErrors.notes = 'Notes cannot exceed 1000 characters';
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Special handling for phone number formatting
    if (name === 'customer_phone') {
      processedValue = formatPhoneNumber(value);
    }

    // Handle numeric fields
    if (name === 'quantity' || name === 'price') {
      processedValue = parseFloat(value) || '';
    }

    // Handle notes character count
    if (name === 'notes') {
      setNotesCharCount(value.length);
    }

    setFormData({
      ...formData,
      [name]: processedValue,
    });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Map form data to backend schema
      const orderData = {
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        customer_po_number: formData.customer_po_number || null,
        item_name: formData.item_name,
        item_sku: formData.item_sku || null,
        item_size: formData.item_size || null,
        quantity: formData.quantity,
        price: formData.price,
        payment_status: formData.payment_status,
        salesperson_id: formData.salesperson_id || null,
        notes: formData.notes,
      };

      await api.post('/special-orders/orders', orderData);
      setSuccess(true);
      // Reset form
      setFormData({
        customer_name: '',
        customer_phone: '',
        customer_po_number: '',
        item_name: '',
        item_sku: '',
        item_size: '',
        quantity: 1,
        price: 0,
        payment_status: 'Not Paid',
        salesperson_id: '',
        notes: '',
      });
      setNotesCharCount(0);
      setErrors({});

      // Redirect after success
      setTimeout(() => {
        window.location.href = '/special-orders';
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="order-form-container">
      <div className="form-header">
        <h1>Create New Order</h1>
        <p>Fill in the details below to create a new special order</p>
      </div>

      {success && (
        <div className="success-message">
          ✓ Order created successfully! Redirecting to dashboard...
        </div>
      )}

      {error && <div className="error-message">✕ {error}</div>}

      <form onSubmit={handleSubmit} className="order-form">
        {/* CUSTOMER INFORMATION */}
        <div className="form-section">
          <h3>Customer Information</h3>

          <div className="form-group">
            <label htmlFor="customer_name">
              Customer Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="customer_name"
              name="customer_name"
              value={formData.customer_name}
              onChange={handleChange}
              maxLength="255"
              placeholder="Enter customer name"
              className={errors.customer_name ? 'input-error' : ''}
            />
            {errors.customer_name && (
              <span className="error-text">{errors.customer_name}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customer_phone">Customer Phone</label>
              <input
                type="tel"
                id="customer_phone"
                name="customer_phone"
                value={formData.customer_phone}
                onChange={handleChange}
                placeholder="123-456-7890"
                className={errors.customer_phone ? 'input-error' : ''}
              />
              {errors.customer_phone && (
                <span className="error-text">{errors.customer_phone}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="customer_po_number">PO Number</label>
              <input
                type="text"
                id="customer_po_number"
                name="customer_po_number"
                value={formData.customer_po_number}
                onChange={handleChange}
                maxLength="100"
                placeholder="Optional PO number"
                className={errors.customer_po_number ? 'input-error' : ''}
              />
              {errors.customer_po_number && (
                <span className="error-text">{errors.customer_po_number}</span>
              )}
            </div>
          </div>
        </div>

        {/* ITEM INFORMATION */}
        <div className="form-section">
          <h3>Item Information</h3>

          <div className="form-group">
            <label htmlFor="item_name">
              Item Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="item_name"
              name="item_name"
              value={formData.item_name}
              onChange={handleChange}
              maxLength="255"
              placeholder="Enter item name"
              className={errors.item_name ? 'input-error' : ''}
            />
            {errors.item_name && (
              <span className="error-text">{errors.item_name}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="item_sku">SKU</label>
              <input
                type="text"
                id="item_sku"
                name="item_sku"
                value={formData.item_sku}
                onChange={handleChange}
                maxLength="100"
                placeholder="Optional SKU"
                className={errors.item_sku ? 'input-error' : ''}
              />
              {errors.item_sku && (
                <span className="error-text">{errors.item_sku}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="item_size">Size</label>
              <input
                type="text"
                id="item_size"
                name="item_size"
                value={formData.item_size}
                onChange={handleChange}
                maxLength="50"
                placeholder="Optional size"
                className={errors.item_size ? 'input-error' : ''}
              />
              {errors.item_size && (
                <span className="error-text">{errors.item_size}</span>
              )}
            </div>
          </div>
        </div>

        {/* ORDER DETAILS */}
        <div className="form-section">
          <h3>Order Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="quantity">
                Quantity <span className="required">*</span>
              </label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                min="0.01"
                step="0.01"
                placeholder="Enter quantity"
                className={errors.quantity ? 'input-error' : ''}
              />
              {errors.quantity && (
                <span className="error-text">{errors.quantity}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="price">
                Price ($) <span className="required">*</span>
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="Enter price"
                className={errors.price ? 'input-error' : ''}
              />
              {errors.price && (
                <span className="error-text">{errors.price}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="payment_status">Payment Status</label>
              <select
                id="payment_status"
                name="payment_status"
                value={formData.payment_status}
                onChange={handleChange}
              >
                <option value="Not Paid">Not Paid</option>
                <option value="Paid in Full">Paid in Full</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="salesperson_id">
              Salesperson <span className="required">*</span>
            </label>
            <select
              id="salesperson_id"
              name="salesperson_id"
              value={formData.salesperson_id}
              onChange={handleChange}
              className={errors.salesperson_id ? 'input-error' : ''}
            >
              <option value="">-- Select Salesperson --</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.display_name || user.username}
                </option>
              ))}
            </select>
            {errors.salesperson_id && (
              <span className="error-text">{errors.salesperson_id}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="notes">
              Notes <span className="char-count">({notesCharCount}/1000)</span>
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              maxLength="1000"
              placeholder="Any special instructions or notes"
              rows="4"
              className={errors.notes ? 'input-error' : ''}
            ></textarea>
            {errors.notes && (
              <span className="error-text">{errors.notes}</span>
            )}
          </div>
        </div>

        {/* FORM ACTIONS */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => (window.location.href = '/special-orders')}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating Order...' : 'Create Order'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrderForm;
